import { useState } from "react";
import { X } from "lucide-react";
import rightmark from "../../../../assets/right-mark-with-background.png";

const rightMarkeImg = (
  <img
    src={rightmark}
    className="mx-autow-5 h-5 sm:w-5 sm:h-5"
    alt="right mark"
  />
);

// Get plan index: base=0, premium=1, premiumplus=2, ultimate=3
const getPlanIndex = (btnValue) => {
  switch (btnValue) {
    case "base": return 0;
    case "premium": return 1;
    case "premiumplus": return 2;
    case "ultimate": return 3;
    default: return 0;
  }
};

// Get value for a specific plan from values array
const getPlanValue = (values, planIndex) => {
  if (!values || values.length === 0) return "";
  if (planIndex >= values.length) return "";
  const value = values[planIndex];
  
  // Check if it's a React element (checkmark image)
  if (value && typeof value === 'object' && (value.type === 'img' || value.$$typeof)) {
    return "check";
  }
  // Check if it's a string checkmark
  if (value === "✓") {
    return "check";
  }
  // Check if it's an X
  if (value === "✗" || value === "x") {
    return "x";
  }
  // Return the value as string
  return value?.toString() || "";
};

// All data sections from compare page
const freeBusinessFeatures = [
  { name: "business chat - open customer engagement", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "in-chat ordering (continuous ordering - pay anytime)", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "in-chat cancellations and refunds", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "order capacity and q management", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "customisable ordering flows", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "hybrid ordering - team members and customers", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "click and collect", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "customer chat prioritisation", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "physical/static & mobile locations", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "", values: [] },
  { name: "multi location management", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "1 location", values: [rightMarkeImg, "✗", "✗", "✗"] },
  { name: "upto 3 location", values: ["✗", rightMarkeImg, "✗", "✗"] },
  { name: "upto 6 locations", values: ["✗", "✗", rightMarkeImg, "✗"] },
  { name: "upto 10 locations", values: ["✗", "✗", "✗", rightMarkeImg] },
  { name: "ratings and reviews", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
];

const freeAIFeature = [
  { name: "in-chat ai customer support", values: ["1k msgs", "40k msgs", "100k msgs", "200k msgs"] },
  { name: "ai order recomendations", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "ai order management/placing including customisation", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "ai booking & reservations", values: ["coming soon", "coming soon", "coming soon", "coming soon"] },
];

const freePaymentSolutions = [
  { name: "business wallet per location", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "manuel or in app payments", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "digital invoices and receipts", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "upfront payments requests", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "payment now requests", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "group ordering and payments", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "bill and invoice splitting", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "customer payment history", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "in-app tipping", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
];

const geoMarketingSolutions = [
  { name: "digital storefront per location", values: ["1", "3", "6", "10"] },
  { name: "full market place access", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "digital menus & digital catalogues", values: ["2", "10", "20", "40"] },
  { name: "team association and visibility", values: ["5", "60", "150", "200"] },
  { name: "social media, posting (per month)", values: ["10", "100", "300", "500"] },
  { name: "likes, following and sharing", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "group chats", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "events (per month)", values: ["5", "25", "60", "150"] },
  { name: "fast marketing via posts and text (additional fee)", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "marketing and engagement insights", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
];

const freeInventSolutions = [
  { name: "menu's and catalogues per location, timed", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "item inventory per location", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "item scanner", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
  { name: "inventory dashboard", values: [rightMarkeImg, rightMarkeImg, rightMarkeImg, rightMarkeImg] },
];

// Convert data array to details array for a specific plan
const convertToDetailsArray = (dataArray, planIndex, expanded = true) => {
  const details = dataArray.map(item => ({
    label: item.name,
    value: getPlanValue(item.values, planIndex)
  }));

  // For collapsed view, filter out location options
  if (!expanded) {
    return details.filter(item => 
      !item.label.includes("location") || 
      item.label === "multi location management" || 
      item.label === "physical/static & mobile locations" ||
      item.label === ""
    );
  }

  return details;
};

const PricingNavContentPage2 = (props) => {
  const [hideElement, setHideElement] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    businessFeatures: false,
    aiFeatures: false,
    paymentSolutions: false,
    geoMarketing: false,
    inventory: false
  });

  const planIndex = getPlanIndex(props.btnContentValue);

  // Get all sections data for the selected plan
  const businessFeaturesData = convertToDetailsArray(freeBusinessFeatures, planIndex, expandedSections.businessFeatures);
  const aiFeaturesData = convertToDetailsArray(freeAIFeature, planIndex);
  const paymentSolutionsData = convertToDetailsArray(freePaymentSolutions, planIndex);
  const geoMarketingData = convertToDetailsArray(geoMarketingSolutions, planIndex);
  const inventoryData = convertToDetailsArray(freeInventSolutions, planIndex);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderDetailRow = (item, index, sectionKey) => {
    const isMultiLocationRow = item.label === "multi location management";
    // Only match actual location options: "1 location", "upto 3 location", "upto 6 locations", "upto 10 locations"
    const isLocationOption = /^(1 location|upto \d+ locations?)$/i.test(item.label.trim());
    const isEmptyRow = item.label === "";

    return (
      <div
        key={index}
        onClick={isMultiLocationRow ? () => toggleSection('businessFeatures') : undefined}
        className={`flex justify-between items-center px-0 sm:text-[12px] sm:ml-[-1.4rem] ${
          isEmptyRow 
            ? "bg-white py-[8px]" 
            : (index % 2 === 0) 
              ? "bg-gray-100 py-3" 
              : "bg-white py-3"
        }`}
      >
        <span className={`font-medium ${isLocationOption ? 'ml-20' : 'ml-10'} text-gray-700`}>
          {item.label}
        </span>

        {isMultiLocationRow ? (
          <span className="text-gray-600 text-sm flex items-center sm:mr-[12px] md:mr-[70px] lg-xl:mr-[120px] xl-2xl:mr-[310px]">
            <span className="mr-2">{expandedSections.businessFeatures ? "▲" : "▼"}</span>
            <div className="w-2 h-2 sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-5 lg:h-5">
              <CheckmarkIconWithBgWhite />
            </div>
          </span>
        ) : (
          <span className="text-gray-600 text-sm flex items-center sm:mr-[12px] md:mr-[70px] lg-xl:mr-[120px] xl-2xl:mr-[310px]">
            {!isEmptyRow && (
              <>
                {item.value === "check" ? (
                  <div className="w-2 h-2 sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-5 lg:h-5">
                    <CheckmarkIconWithBgWhite />
                  </div>
                ) : item.value === "x" ? (
                  <X className="text-black" size={18} />
                ) : item.value && item.value !== "" ? (
                  <span className="text-gray-600 text-sm">{item.value}</span>
                ) : null}
              </>
            )}
          </span>
        )}
      </div>
    );
  };

  const renderSection = (title, data, sectionKey) => {
    return (
      <div key={sectionKey} className="mb-8">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">
          {title}
        </h3>
        <div className="overflow-hidden pb-4">
          {data.map((item, index) => renderDetailRow(item, index, sectionKey))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center justify-center bg-white px-[28px] md:px-[43px] lg-xl:px-[45px] py-30">
      <div className="w-full max-w-[1450px]">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-1 text-gray-900">
            it's all in <span className="text-[#DB8B11] font-bold">the details</span>
          </h2>
        </div>

        {renderSection("free business features", businessFeaturesData, "businessFeatures")}
        {renderSection("free ai features", aiFeaturesData, "aiFeatures")}
        {renderSection("free payment solutions", paymentSolutionsData, "paymentSolutions")}
        {renderSection("geo marketing solutions", geoMarketingData, "geoMarketing")}
        {renderSection("free inventory solutions", inventoryData, "inventory")}
      </div>
    </div>
  );
};

const CheckmarkIconWithBgWhite = () => (
  <svg viewBox="0 0 512 512">
    <circle cx="256" cy="256" r="256" fill="#6E3FC6" />
    <polyline
      points="160,256 230,320 352,192"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="32"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default PricingNavContentPage2;
